import { createRouter, createWebHashHistory } from 'vue-router'
import type {
  Router,
  RouteLocationNormalized,
  RouteRecordNormalized,
  RouteRecordRaw
} from 'vue-router'
import { isUrl } from '@/utils/is'
import { omit, cloneDeep } from 'lodash-es'

const modules = import.meta.glob('../views/**/*.{vue,tsx}')

/* Layout */
export const Layout = () => import('@/layout/Layout.vue')

export const getParentLayout = () => {
  return () =>
    new Promise((resolve) => {
      resolve({
        name: 'ParentLayout'
      })
    })
}

export const getRawRoute = (route: RouteLocationNormalized): RouteLocationNormalized => {
  if (!route) return route
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path
        }))
      : undefined) as RouteRecordNormalized[]
  }
}

// 接口返回的权限菜单数据类型
interface PermissionMenuItem {
  id: number
  parent_id: number
  permission_name: string
  permission_code: string
  path: string | null
  component: string | null
  icon: string | null
  sort: number
  meta: {
    title: string
    hidden?: boolean
    [key: string]: any
  }
  children?: PermissionMenuItem[]
}

/**
 * 根据权限菜单数据生成 vue-router 路由
 * @param permissions 接口返回的权限菜单数据数组
 * @returns 符合 vue-router 格式的路由数组
 */
export const generateRoutesByPermissions = (
  permissions: PermissionMenuItem[]
): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const permission of permissions) {
    const route = transformPermissionToRoute(permission)
    if (route) {
      res.push(route)
    }
  }

  return res
}

/**
 * 将单个权限菜单项转换为路由配置
 */
const transformPermissionToRoute = (permission: PermissionMenuItem): AppRouteRecordRaw | null => {
  // 生成路由名称：优先使用 permission_code，否则基于 path 或 id 生成
  const routeName = permission.permission_code || generateRouteName(permission.path, permission.id)

  // 处理路径
  let routePath = permission.path
  if (!routePath) {
    // 如果 path 为 null，根据情况生成路径
    if (permission.children && permission.children.length > 0) {
      // 有子路由时，尝试从子路由路径中提取共同前缀
      const childPaths = permission.children
        .map((child) => child.path)
        .filter((p): p is string => !!p)
      if (childPaths.length > 0) {
        // 提取共同前缀作为父路径
        const commonPrefix = extractCommonPrefix(childPaths)
        routePath = commonPrefix || `/${permission.permission_code}`
      } else {
        routePath = `/${permission.permission_code}`
      }
    } else {
      // 没有子路由，使用 permission_code 生成路径
      routePath = `/${permission.permission_code}`
    }
  }

  // 构建 meta 信息
  const meta: Record<string, any> = {
    ...permission.meta,
    title: permission.meta?.title || permission.permission_name,
    hidden: permission.meta?.hidden || false
  }

  // 如果 icon 不在 meta 中，且 permission 有 icon，则添加到 meta
  if (permission.icon && !meta.icon) {
    meta.icon = permission.icon
  }

  // 处理组件
  let component: AppRouteRecordRaw['component']
  if (permission.component) {
    const componentPath = permission.component.trim()
    if (componentPath === '#') {
      // # 表示使用 Layout
      component = Layout
    } else if (componentPath === '##') {
      // ## 表示使用 ParentLayout
      component = getParentLayout()
    } else if (componentPath) {
      // 其他情况，尝试动态导入组件
      const comModule = modules[`../${componentPath}.vue`] || modules[`../${componentPath}.tsx`]
      if (comModule) {
        component = comModule
      } else {
        console.warn(`未找到组件文件: ${componentPath}.vue 或 ${componentPath}.tsx`)
        // 如果找不到组件，可以根据实际情况决定是否创建路由
        // 这里先设置为 undefined，让路由可以创建但组件需要后续处理
      }
    }
  } else if (permission.children && permission.children.length > 0) {
    // 没有 component 但有子路由，使用 Layout
    component = Layout
  }

  // 构建路由对象
  const route: AppRouteRecordRaw = {
    path: routePath,
    name: routeName,
    meta,
    component
  }

  // 处理重定向：如果有子路由，重定向到第一个子路由
  if (permission.children && permission.children.length > 0) {
    const firstChildPath = permission.children[0].path
    if (firstChildPath) {
      // 确保重定向路径是完整的
      route.redirect =
        routePath === '/'
          ? firstChildPath.startsWith('/')
            ? firstChildPath
            : `/${firstChildPath}`
          : firstChildPath.startsWith('/')
            ? firstChildPath
            : `${routePath}/${firstChildPath}`.replace(/\/\//g, '/')
    }
  }

  // 递归处理子路由
  if (permission.children && permission.children.length > 0) {
    route.children = []
    for (const child of permission.children) {
      const childRoute = transformPermissionToRoute(child)
      if (childRoute) {
        route.children.push(childRoute)
      }
    }
  }

  return route
}

/**
 * 生成路由名称
 */
const generateRouteName = (path: string | null, id: number): string => {
  if (path) {
    // 基于 path 生成名称：去除斜杠，转换为驼峰命名
    return (
      path
        .replace(/^\//, '')
        .replace(/\//g, '-')
        .replace(/[^a-zA-Z0-9-]/g, '')
        .replace(/-([a-z])/g, (_, letter) => letter.toUpperCase()) || `Route${id}`
    )
  }
  return `Route${id}`
}

/**
 * 从多个路径中提取共同前缀
 */
const extractCommonPrefix = (paths: string[]): string | null => {
  if (paths.length === 0) return null
  if (paths.length === 1) {
    // 单个路径，返回其目录部分
    const path = paths[0]
    const lastSlash = path.lastIndexOf('/')
    return lastSlash > 0 ? path.substring(0, lastSlash) : '/'
  }

  // 找到所有路径的共同前缀
  let prefix = paths[0]
  for (let i = 1; i < paths.length; i++) {
    const path = paths[i]
    let j = 0
    while (j < prefix.length && j < path.length && prefix[j] === path[j]) {
      j++
    }
    prefix = prefix.substring(0, j)
    // 确保前缀在 '/' 处结束
    const lastSlash = prefix.lastIndexOf('/')
    if (lastSlash >= 0) {
      prefix = prefix.substring(0, lastSlash + 1)
    } else {
      prefix = '/'
      break
    }
  }

  return prefix || '/'
}

// 前端控制路由生成
export const generateRoutesByFrontEnd = (
  routes: AppRouteRecordRaw[],
  keys: string[],
  basePath = '/'
): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const meta = route.meta ?? {}
    // skip some route
    if (meta.hidden && !meta.canTo) {
      continue
    }

    let data: Nullable<AppRouteRecordRaw> = null

    let onlyOneChild: Nullable<string> = null
    if (route.children && route.children.length === 1 && !meta.alwaysShow) {
      onlyOneChild = (
        isUrl(route.children[0].path)
          ? route.children[0].path
          : pathResolve(pathResolve(basePath, route.path), route.children[0].path)
      ) as string
    }

    // 开发者可以根据实际情况进行扩展
    for (const item of keys) {
      // 通过路径去匹配
      if (isUrl(item) && (onlyOneChild === item || route.path === item)) {
        data = Object.assign({}, route)
      } else {
        const routePath = (onlyOneChild ?? pathResolve(basePath, route.path)).trim()
        if (routePath === item || meta.followRoute === item) {
          data = Object.assign({}, route)
        }
      }
    }

    // recursive child routes
    if (route.children && data) {
      data.children = generateRoutesByFrontEnd(
        route.children,
        keys,
        pathResolve(basePath, data.path)
      )
    }
    if (data) {
      res.push(data as AppRouteRecordRaw)
    }
  }
  return res
}

// 后端控制路由生成
export const generateRoutesByServer = (routes: AppCustomRouteRecordRaw[]): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const data: AppRouteRecordRaw = {
      path: route.path,
      name: route.name,
      redirect: route.redirect,
      meta: route.meta
    }
    if (route.component) {
      const comModule = modules[`../${route.component}.vue`] || modules[`../${route.component}.tsx`]
      const component = route.component as string
      if (!comModule && !component.includes('#')) {
        console.error(`未找到${route.component}.vue文件或${route.component}.tsx文件，请创建`)
      } else {
        // 动态加载路由文件，可根据实际情况进行自定义逻辑
        data.component =
          component === '#' ? Layout : component.includes('##') ? getParentLayout() : comModule
      }
    }
    // recursive child routes
    if (route.children) {
      data.children = generateRoutesByServer(route.children)
    }
    res.push(data as AppRouteRecordRaw)
  }
  return res
}

export const pathResolve = (parentPath: string, path: string) => {
  if (isUrl(path)) return path
  const childPath = path.startsWith('/') || !path ? path : `/${path}`
  return `${parentPath}${childPath}`.replace(/\/\//g, '/').trim()
}

// 路由降级
export const flatMultiLevelRoutes = (routes: AppRouteRecordRaw[]) => {
  const modules: AppRouteRecordRaw[] = cloneDeep(routes)
  for (let index = 0; index < modules.length; index++) {
    const route = modules[index]
    if (!isMultipleRoute(route)) {
      continue
    }
    promoteRouteLevel(route)
  }
  return modules
}

// 层级是否大于2
const isMultipleRoute = (route: AppRouteRecordRaw) => {
  if (!route || !Reflect.has(route, 'children') || !route.children?.length) {
    return false
  }

  const children = route.children

  let flag = false
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    if (child.children?.length) {
      flag = true
      break
    }
  }
  return flag
}

// 生成二级路由
const promoteRouteLevel = (route: AppRouteRecordRaw) => {
  let router: Router | null = createRouter({
    routes: [route as RouteRecordRaw],
    history: createWebHashHistory()
  })

  const routes = router.getRoutes()
  addToChildren(routes, route.children || [], route)
  router = null

  route.children = route.children?.map((item) => omit(item, 'children'))
}

// 添加所有子菜单
const addToChildren = (
  routes: RouteRecordNormalized[],
  children: AppRouteRecordRaw[],
  routeModule: AppRouteRecordRaw
) => {
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    const route = routes.find((item) => item.name === child.name)
    if (!route) {
      continue
    }
    routeModule.children = routeModule.children || []
    if (!routeModule.children.find((item) => item.name === route.name)) {
      routeModule.children?.push(route as unknown as AppRouteRecordRaw)
    }
    if (child.children?.length) {
      addToChildren(routes, child.children, routeModule)
    }
  }
}
